/**
 * Promotion Templates Index
 * Pre-built promotion templates based on best practices from
 * successful SaaS companies like Notion, Figma, Slack, Dropbox, etc.
 *
 * 35+ templates covering:
 * - Seasonal (New Year, Black Friday, Christmas, etc.)
 * - Flash Sales (24-hour, Weekend, Gamified)
 * - Milestones (Anniversary, Usage, Company)
 * - Referrals (Double-sided, Contest, Team)
 * - Win-Back (Churned, Inactive, Downgrade prevention)
 * - Upgrades (Trial, Free-to-Pro, Annual)
 * - First-Time (Welcome, Bonus, Exit Intent)
 * - Credit Rewards (Daily Login, Streaks, Spin Wheel, Achievements)
 */

// Import all template collections
import { SEASONAL_TEMPLATES } from "./seasonal";
import { FLASH_SALE_TEMPLATES } from "./flashSale";
import { MILESTONE_TEMPLATES } from "./milestone";
import { REFERRAL_TEMPLATES } from "./referral";
import { WIN_BACK_TEMPLATES } from "./winBack";
import { UPGRADE_TEMPLATES } from "./upgrade";
import { FIRST_TIME_TEMPLATES } from "./firstTime";
import { CREDIT_REWARDS_TEMPLATES } from "./creditRewards";

// Re-export types
export * from "./types";

// Re-export individual collections
export { SEASONAL_TEMPLATES } from "./seasonal";
export { FLASH_SALE_TEMPLATES } from "./flashSale";
export { MILESTONE_TEMPLATES } from "./milestone";
export { REFERRAL_TEMPLATES } from "./referral";
export { WIN_BACK_TEMPLATES } from "./winBack";
export { UPGRADE_TEMPLATES } from "./upgrade";
export { FIRST_TIME_TEMPLATES } from "./firstTime";
export { CREDIT_REWARDS_TEMPLATES } from "./creditRewards";

// Combined templates array
export const ALL_PROMOTION_TEMPLATES = [
  ...SEASONAL_TEMPLATES,
  ...FLASH_SALE_TEMPLATES,
  ...MILESTONE_TEMPLATES,
  ...REFERRAL_TEMPLATES,
  ...WIN_BACK_TEMPLATES,
  ...UPGRADE_TEMPLATES,
  ...FIRST_TIME_TEMPLATES,
  ...CREDIT_REWARDS_TEMPLATES,
];

// Template categories for the gallery
export const PROMOTION_TEMPLATE_CATEGORIES = [
  {
    id: "seasonal",
    name: "Seasonal",
    description: "Holiday and seasonal campaigns",
    icon: "CalendarOutlined",
    color: "#8B5CF6",
    templates: SEASONAL_TEMPLATES,
  },
  {
    id: "flash_sale",
    name: "Flash Sale",
    description: "Time-limited offers that create urgency",
    icon: "ThunderboltOutlined",
    color: "#EF4444",
    templates: FLASH_SALE_TEMPLATES,
  },
  {
    id: "milestone",
    name: "Milestones",
    description: "Celebrate user and company achievements",
    icon: "TrophyOutlined",
    color: "#F59E0B",
    templates: MILESTONE_TEMPLATES,
  },
  {
    id: "referral",
    name: "Referral",
    description: "Drive organic growth through referrals",
    icon: "UsergroupAddOutlined",
    color: "#10B981",
    templates: REFERRAL_TEMPLATES,
  },
  {
    id: "win_back",
    name: "Win-Back",
    description: "Re-engage churned or inactive users",
    icon: "HeartOutlined",
    color: "#EC4899",
    templates: WIN_BACK_TEMPLATES,
  },
  {
    id: "upgrade",
    name: "Upgrade",
    description: "Drive plan upgrades and expansions",
    icon: "RocketOutlined",
    color: "#3B82F6",
    templates: UPGRADE_TEMPLATES,
  },
  {
    id: "first_time",
    name: "First-Time",
    description: "Welcome and convert new users",
    icon: "GiftOutlined",
    color: "#06B6D4",
    templates: FIRST_TIME_TEMPLATES,
  },
  {
    id: "credit_rewards",
    name: "Credit Rewards",
    description: "Gamified credit-based engagement",
    icon: "StarOutlined",
    color: "#F59E0B",
    templates: CREDIT_REWARDS_TEMPLATES,
  },
];

// Helper function to get template by ID
export const getPromotionTemplateById = (id: string) => {
  return ALL_PROMOTION_TEMPLATES.find((template) => template.id === id);
};

// Helper function to get templates by category
export const getPromotionTemplatesByCategory = (category: string) => {
  return ALL_PROMOTION_TEMPLATES.filter((template) => template.category === category);
};

// Helper function to get templates by tag
export const getPromotionTemplatesByTag = (tag: string) => {
  return ALL_PROMOTION_TEMPLATES.filter((template) => template.tags.includes(tag));
};

// Helper function to get templates by difficulty
export const getPromotionTemplatesByDifficulty = (difficulty: "beginner" | "intermediate" | "advanced") => {
  return ALL_PROMOTION_TEMPLATES.filter((template) => template.difficulty === difficulty);
};

// Helper function to get gamified templates
export const getGamifiedTemplates = () => {
  return ALL_PROMOTION_TEMPLATES.filter((template) => template.defaults.gamification.type !== "none");
};
